import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const ResultCard = styled(StyledCard)(({ theme, severity }) => ({
  background: severity === 'high' ? 
    'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)' :
    severity === 'medium' ?
    'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)' :
    'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
  border: `2px solid ${
    severity === 'high' ? theme.palette.error.main :
    severity === 'medium' ? theme.palette.warning.main :
    theme.palette.success.main
  }`,
}));

const MetricBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00ff00', '#ff0000', '#0088fe', '#00c49f'];

const TourismCalculationResults = ({ calculation, onAddToComparison, onExport, calculator }) => {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    breakdown: true,
    recommendations: false,
    details: false
  });

  if (!calculation) {
    return (
      <Alert severity="info">
        Hesaplama sonucu bulunamadı. Lütfen önce bir hesaplama yapın.
      </Alert>
    );
  }

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExport = (format) => {
    onExport([calculation.id], format);
  };

  const getSeverityLevel = (total) => {
    if (total > 100) return 'high';
    if (total > 50) return 'medium';
    return 'low';
  };

  const getSeverityColor = (level) => {
    switch (level) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'success';
    }
  };

  const getSeverityIcon = (level) => {
    switch (level) {
      case 'high': return '⚠️';
      case 'medium': return '📈';
      default: return '✅';
    }
  };

  // Pie chart için veri hazırlama
  const pieData = Object.entries(calculation.breakdown)
    .filter(([_, value]) => value > 0)
    .map(([key, value], index) => ({
      name: getCategoryDisplayName(key),
      value: Math.round(value * 100) / 100,
      color: COLORS[index % COLORS.length]
    }));

  // Bar chart için veri hazırlama
  const barData = pieData.map(item => ({
    category: item.name,
    emissions: item.value,
    percentage: ((item.value / calculation.total) * 100).toFixed(1)
  }));

  function getCategoryDisplayName(key) {
    const names = {
      accommodation: 'Konaklama',
      energy: 'Enerji',
      water: 'Su',
      food: 'Yemek & İçecek',
      laundry: 'Çamaşır',
      waste: 'Atık',
      transportation: 'Ulaşım',
      facilities: 'Tesis Hizmetleri'
    };
    return names[key] || key;
  }

  const severityLevel = getSeverityLevel(calculation.total);
  const certificationStatus = calculator.getCertificationStatus(calculation.perRoomNight);

  return (
    <Box>
      {/* Ana Sonuç Kartları */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <ResultCard severity={severityLevel}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" fontWeight="bold">
                  Karbon Ayak İzi Sonuçları
                </Typography>
                <Chip 
                  icon={getSeverityIcon(severityLevel)}
                  label={severityLevel === 'high' ? 'Yüksek Emisyon' : 
                        severityLevel === 'medium' ? 'Orta Emisyon' : 'Düşük Emisyon'}
                  color={getSeverityColor(severityLevel)}
                  variant="filled"
                />
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricBox>
                    <Typography variant="h3" color="primary" fontWeight="bold">
                      {Math.round(calculation.total)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Toplam CO₂e (kg)
                    </Typography>
                  </MetricBox>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <MetricBox>
                    <Typography variant="h3" color="success.main" fontWeight="bold">
                      {Math.round(calculation.perRoomNight)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Oda-Gece Başına (kg)
                    </Typography>
                  </MetricBox>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <MetricBox>
                    <Typography variant="h4" color="warning.main" fontWeight="bold">
                      {calculation.period === 'daily' ? 'Günlük' :
                       calculation.period === 'monthly' ? 'Aylık' : 'Yıllık'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Hesaplama Periyodu
                    </Typography>
                  </MetricBox>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <MetricBox>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {calculation.season === 'spring' ? 'İlkbahar' :
                       calculation.season === 'summer' ? 'Yaz' :
                       calculation.season === 'autumn' ? 'Sonbahar' : 'Kış'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Mevsim
                    </Typography>
                  </MetricBox>
                </Grid>
              </Grid>
            </CardContent>
          </ResultCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Hızlı İşlemler
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  onClick={() => onAddToComparison(calculation)}
                  fullWidth
                >
                  ⚖️ Karşılaştırmaya Ekle
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => handleExport('pdf')}
                  fullWidth
                >
                  📄 PDF İndir
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => handleExport('csv')}
                  fullWidth
                >
                  📊 CSV İndir
                </Button>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Sertifikasyon Durumu
              </Typography>
              <Alert 
                severity={calculation.perRoomNight <= 12 ? 'success' : 'warning'}
              >
                🌱 {certificationStatus.status}
              </Alert>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Detaylı Analiz */}
      <Accordion expanded={expandedSections.breakdown} onChange={() => handleSectionToggle('breakdown')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📊 Kategori Bazında Dağılım</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Pie Chart */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" align="center" gutterBottom>
                Emisyon Dağılımı
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => [`${value} kg CO₂e`, 'Emisyon']} />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
            
            {/* Bar Chart */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" align="center" gutterBottom>
                Kategori Karşılaştırması
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                  />
                  <YAxis />
                  <RechartsTooltip formatter={(value) => [`${value} kg CO₂e`, 'Emisyon']} />
                  <Bar dataKey="emissions" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Detaylı Tablo */}
      <Accordion expanded={expandedSections.details} onChange={() => handleSectionToggle('details')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">📋 Detaylı Emisyon Tablosu</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kategori</TableCell>
                  <TableCell align="right">Emisyon (kg CO₂e)</TableCell>
                  <TableCell align="right">Yüzde (%)</TableCell>
                  <TableCell align="right">Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(calculation.breakdown).map(([key, value]) => {
                  const percentage = ((value / calculation.total) * 100).toFixed(1);
                  return (
                    <TableRow key={key}>
                      <TableCell>{getCategoryDisplayName(key)}</TableCell>
                      <TableCell align="right">{Math.round(value * 100) / 100}</TableCell>
                      <TableCell align="right">{percentage}%</TableCell>
                      <TableCell align="right">
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(percentage, 100)} 
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOPLAM</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {Math.round(calculation.total * 100) / 100}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>100%</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label="Toplam" 
                      color="primary" 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Öneriler */}
      {calculation.recommendations && calculation.recommendations.length > 0 && (
        <Accordion expanded={expandedSections.recommendations} onChange={() => handleSectionToggle('recommendations')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">💡 İyileştirme Önerileri</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {calculation.recommendations.map((rec, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip 
                          label={rec.priority} 
                          color={rec.priority === 'Yüksek' ? 'error' : 
                                rec.priority === 'Orta' ? 'warning' : 'success'}
                          size="small"
                        />
                        <Typography variant="h6">{rec.category}</Typography>
                      </Box>
                      <Typography variant="body2" paragraph>
                        {rec.suggestion}
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        Potansiyel tasarruf: {rec.potentialSaving} kg CO₂e
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default TourismCalculationResults;
